import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, History, Info, RotateCcw, ShieldCheck, UserCheck, Users, Eye, } from "lucide-react";
import { format, parseISO } from "date-fns";
import { PageShell, PageHeader } from "@/components/layout/PageShell";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Tabs } from "@/components/ui/Tabs";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Badge, StatusBadge } from "@/components/ui/Badge";
import { useAuth } from "@/auth/AuthProvider";
import { useManager } from "@/hooks/useManager";
import { getDataAdapter } from "@/data";
import { formatEntryDuration } from "@/utils/duration";
import { TimeEntryDetails } from "./TimeEntryDetails";
import { emitDataEvent, useDataEvent } from "@/data/events";
export function Approvals() {
    const adapter = getDataAdapter();
    const { user, roles } = useAuth();
    const { manager, loaded } = useManager();
    const hasManager = !!manager;
    const [tab, setTab] = useState("manager");
    const [managerQ, setManagerQ] = useState([]);
    const [selfQ, setSelfQ] = useState([]);
    const [delegatedQ, setDelegatedQ] = useState([]);
    const [resources, setResources] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [returnFor, setReturnFor] = useState(null);
    const [returnComment, setReturnComment] = useState("");
    const [showAudit, setShowAudit] = useState(null);
    const [audit, setAudit] = useState([]);
    const [auditLoading, setAuditLoading] = useState(false);
    const [auditError, setAuditError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [viewing, setViewing] = useState(null);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const refresh = useCallback(() => {
        setSelected(new Set());
        setViewing(null);
        setReloadKey((value) => value + 1);
    }, []);
    useDataEvent("entries-changed", refresh);
    const delegationRole = roles.includes("Finance.Approver")
        ? "Finance.Approver"
        : roles.includes("Operations.Manager")
            ? "Operations.Manager"
            : roles.includes("Project.Manager")
                ? "Project.Manager"
                : roles.includes("Admin")
                    ? "Admin"
                    : undefined;
    useEffect(() => {
        let alive = true;
        setLoading(true);
        setError(null);
        void Promise.all([
            adapter.listPendingForManager(user.id),
            adapter.listPendingForSelf(user.id),
            delegationRole
                ? adapter.listPendingDelegated(buildScope(delegationRole, user))
                : Promise.resolve([]),
            adapter.listResources(),
            adapter.listTransactionCategories(),
        ]).then(([m, s, d, people, services]) => {
            if (!alive)
                return;
            setManagerQ(m);
            setSelfQ(s);
            setDelegatedQ(d);
            setResources(people);
            setCategories(services);
            setSelected(new Set());
        }).catch((err) => {
            if (!alive)
                return;
            console.error("Approval queues failed", err);
            setError(err instanceof Error ? err.message : String(err));
            setManagerQ([]);
            setSelfQ([]);
            setDelegatedQ([]);
        }).finally(() => { if (alive)
            setLoading(false); });
        return () => {
            alive = false;
        };
    }, [adapter, user, delegationRole, reloadKey]);
    useEffect(() => {
        if (!showAudit)
            return;
        let alive = true;
        setAudit([]);
        setAuditError(null);
        setAuditLoading(true);
        void adapter.listAnnotations(showAudit.msdyn_timeentryid).then((annotations) => {
            if (alive)
                setAudit(annotations);
        }).catch((err) => {
            if (!alive)
                return;
            console.error("Could not load approval audit", err);
            setAuditError(err instanceof Error ? err.message : String(err));
        }).finally(() => { if (alive)
            setAuditLoading(false); });
        return () => { alive = false; };
    }, [adapter, showAudit]);
    // Self-approve tab visibility: only when user has no manager.
    const tabs = useMemo(() => {
        const list = [
            { id: "manager", label: "My approvals", count: managerQ.length },
        ];
        if (loaded && !hasManager) {
            list.push({ id: "self", label: "Self-approve", count: selfQ.length });
        }
        if (delegationRole) {
            list.push({ id: "delegated", label: "All approvals (delegated)", count: delegatedQ.length });
        }
        return list;
    }, [managerQ.length, selfQ.length, delegatedQ.length, hasManager, loaded, delegationRole]);
    // Reset selection on tab change.
    useEffect(() => setSelected(new Set()), [tab]);
    const queue = tab === "manager" ? managerQ : tab === "self" ? selfQ : delegatedQ;
    const toggle = (id) => {
        const next = new Set(selected);
        if (next.has(id))
            next.delete(id);
        else
            next.add(id);
        setSelected(next);
    };
    const toggleAll = () => {
        if (selected.size === queue.length)
            setSelected(new Set());
        else
            setSelected(new Set(queue.map((e) => e.msdyn_timeentryid)));
    };
    const approve = async (ids) => {
        if (ids.length === 0 || busy)
            return;
        setBusy(true);
        setError(null);
        try {
            if (tab === "self") {
                await adapter.selfApproveEntries(ids, { actorName: user.name, actorUserId: user.id });
            }
            else {
                await adapter.approveEntries(ids, {
                    actorName: user.name,
                    actorUserId: user.id,
                    delegationRole: tab === "delegated" ? delegationRole : undefined,
                });
            }
            emitDataEvent("entries-changed");
        }
        catch (err) {
            console.error("Approval failed", err);
            setError(err instanceof Error ? err.message : String(err));
        }
        finally {
            setBusy(false);
        }
    };
    const submitReturn = async () => {
        if (!returnFor || returnFor.length === 0 || busy)
            return;
        setBusy(true);
        setError(null);
        try {
            await adapter.returnEntries(returnFor.map((e) => e.msdyn_timeentryid), {
                actorName: user.name,
                actorUserId: user.id,
                delegationRole: tab === "delegated" ? delegationRole : undefined,
                comment: returnComment,
            }, returnComment);
            setReturnFor(null);
            setReturnComment("");
            emitDataEvent("entries-changed");
        }
        catch (err) {
            console.error("Return failed", err);
            setError(err instanceof Error ? err.message : String(err));
        }
        finally {
            setBusy(false);
        }
    };
    const resourceName = (id) => resources.find((r) => r.bookableresourceid === id)?.name ?? id;
    return (_jsxs(PageShell, { children: [_jsx(PageHeader, { icon: _jsx(ShieldCheck, { size: 20 }), title: "Approvals", subtitle: "Review submitted time. Approvals write a Dataverse annotation for audit.", right: _jsxs(Badge, { tone: "indigo", children: [_jsx(Users, { size: 12 }), " ", queue.length, " pending"] }) }), error && _jsxs("div", { role: "alert", className: "mb-4 text-sm text-rose-700", children: [error, " ", _jsx(Button, { variant: "ghost", onClick: refresh, children: "Refresh" })] }), _jsxs(Card, { className: "p-0", children: [_jsx("div", { className: "px-4", children: _jsx(Tabs, { tabs: tabs, active: tab, onChange: (id) => { if (!busy) {
                                setTab(id);
                                setViewing(null);
                                setError(null);
                            } } }) }), tab === "self" && (_jsxs("div", { className: "m-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800", children: [_jsx(AlertTriangle, { size: 16, className: "mt-0.5 shrink-0" }), _jsx("div", { children: "You don't have a manager assigned in the directory, so you may self-approve your own entries. Each self-approval writes a Dataverse annotation with the subject \"Self-approved\" for audit." })] })), tab === "delegated" && (_jsxs("div", { className: "m-4 flex items-start gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-800", children: [_jsx(Info, { size: 16, className: "mt-0.5 shrink-0" }), _jsxs("div", { children: ["You are reviewing as ", _jsx("span", { className: "font-semibold", children: delegationRole }), ". Audit annotations include the delegation role."] })] })), _jsxs("div", { className: "flex items-center justify-between px-4 py-3 border-y border-slate-100 bg-slate-50/60", children: [_jsxs("label", { className: "flex items-center gap-2 text-sm text-slate-600", children: [_jsx("input", { type: "checkbox", className: "h-4 w-4 rounded border-slate-300 text-brand-500 focus:ring-brand-200", checked: selected.size > 0 && selected.size === queue.length, onChange: toggleAll, disabled: busy || loading }), _jsxs("span", { children: [selected.size, " selected"] })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Button, { variant: "ghost", onClick: () => {
                                            const list = queue.filter((e) => selected.has(e.msdyn_timeentryid));
                                            setReturnFor(list);
                                        }, disabled: selected.size === 0 || busy || loading, leftIcon: _jsx(RotateCcw, { size: 14 }), children: "Return selected" }), _jsx(Button, { onClick: () => approve(Array.from(selected)), disabled: selected.size === 0 || busy || loading, leftIcon: tab === "self" ? _jsx(UserCheck, { size: 14 }) : _jsx(Check, { size: 14 }), children: tab === "self" ? "Self-approve selected" : "Approve selected" })] })] }), loading && _jsx("p", { role: "status", className: "px-4 py-3 text-sm text-slate-600", children: "Loading approval entries..." }), _jsx("div", { className: "overflow-x-auto", children: _jsxs("table", { className: "w-full text-sm", children: [_jsx("thead", { className: "bg-slate-50 text-xs uppercase tracking-wide text-slate-500", children: _jsxs("tr", { children: [_jsx("th", { className: "px-4 py-2.5 w-8" }), _jsx("th", { className: "text-left px-3 py-2.5 font-medium", children: "Submitted by" }), _jsx("th", { className: "text-left px-3 py-2.5 font-medium", children: "Date" }), _jsx("th", { className: "text-left px-3 py-2.5 font-medium", children: "Description" }), _jsx("th", { className: "text-left px-3 py-2.5 font-medium", children: "Regarding" }), _jsx("th", { className: "text-right px-3 py-2.5 font-medium", children: "Duration" }), _jsx("th", { className: "text-right px-3 py-2.5 font-medium", children: "Status" }), _jsx("th", { className: "px-3 py-2.5 w-16" })] }) }), _jsxs("tbody", { className: "divide-y divide-slate-100", children: [queue.map((e) => (_jsxs("tr", { className: "hover:bg-slate-50/50", children: [_jsx("td", { className: "px-4 py-2.5", children: _jsx("input", { type: "checkbox", checked: selected.has(e.msdyn_timeentryid), onChange: () => toggle(e.msdyn_timeentryid), disabled: busy || loading, "aria-label": `Select ${e.msdyn_description}`, className: "h-4 w-4 rounded border-slate-300 text-brand-500 focus:ring-brand-200" }) }), _jsx("td", { className: "px-3 py-2.5 text-slate-700", children: resourceName(e.bookableresource) }), _jsx("td", { className: "px-3 py-2.5 text-slate-600 whitespace-nowrap", children: format(parseISO(e.msdyn_start), "d MMM yyyy") }), _jsx("td", { className: "px-3 py-2.5 font-medium", children: _jsx("button", { className: "text-left text-brand-700 hover:underline", onClick: () => { setError(null); setViewing(e); }, disabled: busy || loading, children: e.msdyn_description }) }), _jsx("td", { className: "px-3 py-2.5 text-slate-600", children: e.regardingobjectid?.display ?? "—" }), _jsx("td", { className: "px-3 py-2.5 text-right tabular-nums", children: formatEntryDuration(e, categories) }), _jsx("td", { className: "px-3 py-2.5 text-right", children: _jsx(StatusBadge, { status: e.msdyn_entryStatus }) }), _jsxs("td", { className: "px-3 py-2.5 text-right", children: [_jsx(Button, { variant: "subtle", title: `View details for ${e.msdyn_description}`, onClick: () => { setError(null); setViewing(e); }, disabled: busy || loading, children: _jsx(Eye, { size: 14 }) }), _jsx(Button, { variant: "subtle", title: `View audit for ${e.msdyn_description}`, onClick: () => setShowAudit(e), disabled: busy || loading, children: _jsx(History, { size: 14 }) })] })] }, e.msdyn_timeentryid))), !loading && !error && queue.length === 0 && (_jsx("tr", { children: _jsx("td", { colSpan: 8, className: "px-3 py-10 text-center text-slate-400", children: "Nothing waiting for approval. Nice work." }) }))] })] }) })] }), viewing && _jsx(TimeEntryDetails, { entry: viewing, employee: resourceName(viewing.bookableresource), onClose: () => { if (!busy)
                    setViewing(null); }, footer: _jsxs(_Fragment, { children: [error && _jsx("span", { role: "alert", className: "text-xs text-rose-700", children: error }), _jsx(Button, { variant: "ghost", disabled: busy, onClick: () => { setReturnFor([viewing]); setViewing(null); setError(null); }, children: "Return" }), _jsx(Button, { disabled: busy, loading: busy, onClick: () => approve([viewing.msdyn_timeentryid]), children: tab === "self" ? "Self-approve entry" : "Approve entry" })] }) }), _jsxs(Modal, { open: returnFor !== null, onClose: () => {
                    if (busy)
                        return;
                    setReturnFor(null);
                    setReturnComment("");
                }, title: "Return entries", subtitle: "Add a comment explaining what the submitter should change. The comment is saved as a Dataverse annotation.", icon: _jsx(RotateCcw, { size: 16 }), footer: _jsxs(_Fragment, { children: [_jsx(Button, { variant: "ghost", disabled: busy, onClick: () => setReturnFor(null), children: "Cancel" }), _jsxs(Button, { variant: "danger", onClick: submitReturn, disabled: !returnComment.trim() || busy, loading: busy, children: ["Return ", returnFor?.length ?? 0, " entry", (returnFor?.length ?? 0) === 1 ? "" : "ies"] })] }), children: [error && _jsx("p", { role: "alert", className: "text-sm text-rose-700", children: error }), _jsx(Input, { label: "Reason", placeholder: "e.g. Please attach the work order reference.", value: returnComment, onChange: (e) => setReturnComment(e.target.value) })] }), _jsx(Modal, { open: showAudit !== null, onClose: () => setShowAudit(null), title: "Audit log", subtitle: showAudit?.msdyn_description, icon: _jsx(History, { size: 16 }), size: "lg", children: auditLoading ? _jsx("p", { role: "status", className: "text-sm text-slate-600", children: "Loading audit history..." })
                    : auditError ? _jsxs("p", { role: "alert", className: "text-sm text-rose-700", children: ["Could not load audit history: ", auditError, ". Close and reopen to retry."] })
                        : audit.length === 0 ? (_jsx("div", { className: "text-sm text-slate-500", children: "No annotations yet for this entry." })) : (_jsx("ul", { className: "space-y-3", children: audit.map((a) => (_jsxs("li", { className: "rounded-lg border border-slate-200 p-3", children: [_jsxs("div", { className: "flex items-center justify-between text-sm", children: [_jsx("span", { className: "font-medium text-slate-900", children: a.subject }), _jsx("span", { className: "text-xs text-slate-500", children: format(parseISO(a.createdon), "d MMM yyyy, HH:mm") })] }), _jsxs("div", { className: "text-xs text-slate-500", children: ["by ", a.createdbyName] }), a.notetext && _jsx("div", { className: "mt-2 text-sm text-slate-700", children: a.notetext })] }, a.annotationid))) })) })] }));
}
function buildScope(role, user) {
    switch (role) {
        case "Finance.Approver":
            return { role };
        case "Admin":
            return { role };
        case "Operations.Manager":
            return { role, businessUnitId: user.businessUnitId ?? "bu-root" };
        case "Project.Manager":
            return { role, managerUserId: user.id };
    }
}
